import React from 'react';
import {
  ApiBlueprint,
  createApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import {
  type OAuthApi,
  type OpenIdConnectApi,
  type ProfileInfoApi,
  type BackstageIdentityApi,
  type SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';

// Generic OIDC API ref. Backstage doesn't ship one (it ships per-vendor refs
// like `oneloginAuthApiRef`), but the dedicated OneLogin module hardcodes
// scope='openid' and ignores everything else. We wire OneLogin through the
// generic OIDC backend provider and expose this `oidc` API on the frontend.
export const oidcAuthApiRef = createApiRef<
  OAuthApi & OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({
  id: 'auth.oidc',
});

const oidcApi = ApiBlueprint.make({
  name: 'oidc-auth',
  params: defineParams =>
    defineParams({
      api: oidcAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi }) =>
        OAuth2.create({
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'oidc',
            title: 'OneLogin',
            icon: () => null,
          },
          environment: 'production',
          // OneLogin's tenant doesn't advertise `offline_access` in its
          // discovery doc, so requesting it makes OneLogin abort the popup
          // with "scopes not authorized". We only ask for what's listed in
          // scopes_supported.
          defaultScopes: ['openid', 'email', 'profile'],
        }),
    }),
});

const signInPage = SignInPageBlueprint.make({
  name: 'onelogin-sign-in',
  params: {
    loader: async () => {
      const { SignInPage } = await import('@backstage/core-components');
      const provider = {
        id: 'oidc',
        title: 'OneLogin',
        message: 'Sign in with OneLogin SSO',
        apiRef: oidcAuthApiRef,
      };
      return (props: any) =>
        React.createElement(SignInPage, {
          ...props,
          providers: [provider],
        });
    },
  },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [oidcApi, signInPage],
});
